import {
  DestroyRef,
  Signal,
  WritableSignal,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';

import { COUNT_UP } from '../Consts/count-up.const';
import { CountUpOptions } from '../Interfaces/count-up-options.interface';
import { prefersReducedMotion } from './av-chart-dom.util';
import { durationToMs } from './wait-state.util';

function easeOutCubic(elapsed: number): number {
  return 1 - Math.pow(1 - elapsed, 3);
}

function readCountUpDuration(): number {
  const styles = getComputedStyle(document.documentElement);
  const raw = styles.getPropertyValue(COUNT_UP.durationToken);
  return durationToMs(raw) ?? COUNT_UP.fallbackDurationMs;
}

// A hidden tab never fires animation frames, so the figures would sit at zero.
function isNobodyWatching(): boolean {
  return prefersReducedMotion() || document.visibilityState === 'hidden';
}

function playEasedProgress(progress: WritableSignal<number>, duration: number): () => void {
  let frame = 0;
  let origin: number | null = null;

  const tick = (now: number): void => {
    origin ??= now;
    const elapsed = Math.min(1, (now - origin) / Math.max(duration, 1));
    progress.set(easeOutCubic(elapsed));
    if (elapsed < 1) frame = requestAnimationFrame(tick);
  };

  frame = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(frame);
}

export function createCountUpProgress(options: CountUpOptions): Signal<number> {
  const progress = signal(0);
  let started = false;
  let cancelPlayback = (): void => undefined;

  inject(DestroyRef).onDestroy(() => cancelPlayback());

  effect(() => {
    if (started || !options.ready()) return;
    started = true;
    untracked(() => {
      if (isNobodyWatching()) progress.set(1);
      else cancelPlayback = playEasedProgress(progress, readCountUpDuration());
    });
  });

  return progress.asReadonly();
}
